import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCookie, CONFIG, authHeaders } from '../utils/config';

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [needsVerification, setNeedsVerification] = useState(false);
  const navigate = useNavigate();

  const isTokenExpired = (token) => {
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      return true;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = getCookie('quikr_access_token') || localStorage.getItem('quikr_access_token');
      
      const requiresAuth = window.location.pathname.startsWith('/dashboard');

      if (!token || isTokenExpired(token)) {
        const currentPath = window.location.pathname + window.location.search;
        const redirectParam = encodeURIComponent(currentPath);
        localStorage.removeItem('quikr_access_token');
        localStorage.removeItem('quikr_email_verified');
        document.cookie = 'quikr_access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        setIsAuthenticated(false);
        setNeedsVerification(false);
        setIsLoading(false);
        
        if (requiresAuth && !window.location.pathname.startsWith('/login')) {
          navigate(
            `/login?redirect=${redirectParam}&message=${encodeURIComponent('Votre session a expiré. Veuillez vous reconnecter.')}`,
            { replace: true }
          );
        }
        return;
      }

      try {
        const response = await fetch(`${CONFIG.BASE_URL}/account`, {
          method: 'GET',
          headers: authHeaders(),
        });
        if (response.status === 401 || response.status === 403) {
          throw new Error('Unauthorized');
        }
      } catch (error) {
        const currentPath = window.location.pathname + window.location.search;
        const redirectParam = encodeURIComponent(currentPath);
        localStorage.removeItem('quikr_access_token');
        localStorage.removeItem('quikr_email_verified');
        document.cookie = 'quikr_access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        setIsAuthenticated(false);
        setNeedsVerification(false);
        setIsLoading(false);
        if (requiresAuth && !window.location.pathname.startsWith('/login')) {
          navigate(
            `/login?redirect=${redirectParam}&message=${encodeURIComponent('Votre session a expiré. Veuillez vous reconnecter.')}`,
            { replace: true }
          );
        }
        return;
      }

      const emailVerified = localStorage.getItem('quikr_email_verified') === 'true';
      if (!emailVerified) {
        setNeedsVerification(true);
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }
      
      setNeedsVerification(false);
      setIsAuthenticated(true);
      setIsLoading(false);
    };

    checkAuth();

    const handleStorageChange = () => {
      checkAuth();
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const logout = () => {
    localStorage.removeItem('quikr_access_token');
    localStorage.removeItem('quikr_email_verified');
    document.cookie = 'quikr_access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    setIsAuthenticated(false);
    setNeedsVerification(false);
    navigate('/login', { replace: true });
  };

  return {
    isAuthenticated,
    isLoading,
    needsVerification,
    logout
  };
};
